// src/services/onlineUsersService.ts

import apiClient from "./apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export type OnlinePresenceStatus =
  | "ONLINE"
  | "BUSY"
  | "DND"
  | "AWAY"
  | "WATCHING"
  | "BATHROOM"
  | "INVISIBLE";

export interface OnlineUserItem {
  id: number;
  username: string;
  role: string;
  avatarUrl: string | null;
  messagePrivacy: "EVERYONE" | "AUTHORS" | "FOLLOWERS" | "NOBODY";
  /** User's selected presence status. INVISIBLE only appears for MOD/ADMIN callers. */
  presenceStatus: OnlinePresenceStatus;
}

export interface OnlineStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  thisYear: number;
  total: number;
}

// ─── Presence status metadata ─────────────────────────────────────────────────

export interface PresenceStatusMeta {
  status: OnlinePresenceStatus;
  /** i18n key for the label */
  labelKey: string;
  /** Dot/icon color (CSS variable or hex) */
  color: string;
  /** Unicode emoji or icon identifier used in the picker */
  emoji: string;
}

export const PRESENCE_STATUS_LIST: PresenceStatusMeta[] = [
  { status: "ONLINE",   labelKey: "presence_online",   color: "var(--color-success)",  emoji: "✅" },
  { status: "BUSY",     labelKey: "presence_busy",     color: "var(--color-warning)",  emoji: "🟡" },
  { status: "DND",      labelKey: "presence_dnd",      color: "var(--color-error)",    emoji: "🔴" },
  { status: "AWAY",     labelKey: "presence_away",     color: "#7b68ee",               emoji: "🌙" },
  { status: "WATCHING", labelKey: "presence_watching", color: "var(--color-info)",     emoji: "📺" },
  { status: "BATHROOM", labelKey: "presence_bathroom", color: "var(--text-muted)",     emoji: "🚽" },
  { status: "INVISIBLE",labelKey: "presence_invisible",color: "var(--text-muted)",     emoji: "👻" },
];

/** Returns the metadata for a given status value, defaulting to ONLINE. */
export const getPresenceMeta = (status: OnlinePresenceStatus): PresenceStatusMeta =>
  PRESENCE_STATUS_LIST.find(p => p.status === status) ?? PRESENCE_STATUS_LIST[0];

// ─── API ──────────────────────────────────────────────────────────────────────

/**
 * Fetch the list of currently online users.
 * Returns users who are connected via WebSocket and have showOnlineStatus enabled.
 * INVISIBLE users are included only when the caller is MOD/ADMIN (backend filters).
 * Sorted alphabetically by username (backend handles sorting).
 */
export const getOnlineUsers = (): Promise<OnlineUserItem[]> =>
  apiClient.get<OnlineUserItem[]>("/api/users/online");

/**
 * Fetch visitor activity stats for the Online Users modal footer.
 * Counts are based on last_login_date for ACTIVE users.
 */
export const getOnlineStats = (): Promise<OnlineStats> =>
  apiClient.get<OnlineStats>("/api/users/online/stats");

/**
 * Update the current user's online presence status.
 * Persists to DB and triggers a WebSocket broadcast so other users' modals
 * update the changed row in real time.
 */
export const updatePresenceStatus = (status: OnlinePresenceStatus): Promise<{ status: string }> =>
  apiClient.put<{ status: string }>("/api/users/me/presence-status", { status });